/**
 * bugReportCapture — Module de capture client pour les signalements de bug.
 *
 * Init une seule fois au mount de l'App via initBugReportCapture(). Maintient
 * deux ring buffers (console errors/warnings + network failures) en arrière-plan.
 *
 * Quand l'utilisateur soumet un signalement via le HelpPanel, captureContext()
 * retourne tout le contexte technique + un screenshot DOM via html2canvas (lazy
 * import pour ne pas alourdir le bundle initial).
 *
 * Audience interne uniquement (équipe METERIS) — pas de filtrage PII strict.
 * Voir plan ~/.claude/plans/declarative-jingling-riddle.md (PR 2).
 */

const CONSOLE_BUFFER_SIZE = 20;
const NETWORK_BUFFER_SIZE = 10;
const APP_STATE_MAX_BYTES = 50_000;
const RESPONSE_EXCERPT_MAX_CHARS = 500;

// Safelist explicite : seules ces clés localStorage peuvent être snapshot.
// Ne JAMAIS étendre vers des clés contenant des tokens (jwt, vault, etc.).
const LOCALSTORAGE_SAFELIST = [
  'tk_selected_org',
  // Clé réellement écrite par la Sidebar (l'ancienne « meteris_recent_routes »
  // n'était alimentée par rien : les pages récentes manquaient au rapport).
  'sidebar_recent_pages',
  'meteris_help_button_pos',
];

const consoleRingBuffer = [];
const networkRingBuffer = [];

let initialized = false;
let _origConsoleError = null;
let _origConsoleWarn = null;
let _origFetch = null;

export function initBugReportCapture() {
  if (initialized) return;
  initialized = true;

  _origConsoleError = console.error.bind(console);
  _origConsoleWarn = console.warn.bind(console);
  _origFetch = window.fetch.bind(window);

  console.error = function patchedConsoleError(...args) {
    _pushConsole('error', args, _safeStack());
    return _origConsoleError(...args);
  };
  console.warn = function patchedConsoleWarn(...args) {
    _pushConsole('warn', args, null);
    return _origConsoleWarn(...args);
  };

  window.addEventListener('error', (event) => {
    _pushConsole(
      'error',
      [event.message || 'Erreur sans message'],
      event.error?.stack || null,
    );
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = (reason && reason.message) ? reason.message : String(reason);
    _pushConsole('error', [`Promise rejected: ${msg}`], reason?.stack || null);
  });

  window.fetch = async function patchedFetch(input, init) {
    const startTs = Date.now();
    const method = (init && init.method) ? String(init.method).toUpperCase() : 'GET';
    let url = '';
    try {
      url = typeof input === 'string'
        ? input
        : (input && input.url) ? input.url : String(input);
    } catch (_) {
      url = '<unknown>';
    }
    try {
      const response = await _origFetch(input, init);
      if (response && response.status >= 400) {
        let excerpt = '';
        try {
          const clone = response.clone();
          const text = await clone.text();
          excerpt = (text || '').slice(0, RESPONSE_EXCERPT_MAX_CHARS);
        } catch (_) { /* ignore */ }
        _pushNetwork({
          method,
          url,
          status: response.status,
          ts: new Date().toISOString(),
          duration_ms: Date.now() - startTs,
          response_excerpt: excerpt,
        });
      }
      return response;
    } catch (err) {
      _pushNetwork({
        method,
        url,
        status: 0,
        ts: new Date().toISOString(),
        duration_ms: Date.now() - startTs,
        response_excerpt: `Network error: ${err && err.message ? err.message : String(err)}`,
      });
      throw err;
    }
  };
}

function _pushConsole(level, args, stack) {
  const serialized = args.map(_serializeArg);
  consoleRingBuffer.push({
    level,
    ts: new Date().toISOString(),
    args: serialized,
    stack: stack || null,
  });
  while (consoleRingBuffer.length > CONSOLE_BUFFER_SIZE) {
    consoleRingBuffer.shift();
  }
}

function _pushNetwork(entry) {
  networkRingBuffer.push(entry);
  while (networkRingBuffer.length > NETWORK_BUFFER_SIZE) {
    networkRingBuffer.shift();
  }
}

function _serializeArg(arg) {
  if (arg == null) return String(arg);
  if (typeof arg === 'string') return arg;
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  try {
    return JSON.stringify(arg, _safeReplacer);
  } catch (_) {
    try { return String(arg); }
    catch { return '[unserializable]'; }
  }
}

function _safeReplacer() {
  const seen = new WeakSet();
  return function replacer(_, value) {
    if (typeof value === 'function') return '[Function]';
    if (typeof Element !== 'undefined' && value instanceof Element) return '[DOM]';
    if (typeof Node !== 'undefined' && value instanceof Node) return '[Node]';
    if (value && typeof value === 'object') {
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
    }
    return value;
  };
}

function _safeStack() {
  try { return new Error().stack || null; }
  catch { return null; }
}

function _safeLocalStorageSnapshot() {
  const result = {};
  for (const key of LOCALSTORAGE_SAFELIST) {
    try {
      const v = localStorage.getItem(key);
      if (v != null) {
        try { result[key] = JSON.parse(v); }
        catch { result[key] = v; }
      }
    } catch (_) { /* ignore */ }
  }
  return result;
}

function _truncateAppState(obj) {
  let str = '';
  try {
    str = JSON.stringify(obj, _safeReplacer());
  } catch {
    return { _serialize_error: true };
  }
  if (str.length <= APP_STATE_MAX_BYTES) return obj;
  return {
    _truncated: true,
    _original_size_bytes: str.length,
    keys: Object.keys(obj || {}),
  };
}

/**
 * Retourne le bundle complet de contexte à envoyer avec le signalement.
 *
 * @param {Object} opts
 * @param {boolean} opts.includeScreenshot - Si true (défaut), capture le DOM via html2canvas.
 * @param {boolean} opts.light - Si true (idées), n'inclut PAS console_log ni network_failures.
 * @returns {Promise<{capture: Object, console_log: Array, network_failures: Array, app_state: Object, screenshot_blob: Blob|null}>}
 */
export async function captureContext({ includeScreenshot = true, light = false } = {}) {
  const localStorage_snap = _safeLocalStorageSnapshot();
  const appState = _truncateAppState({
    active_org: localStorage_snap.tk_selected_org || null,
    localstorage_snap: localStorage_snap,
  });

  const out = {
    capture: {
      url: window.location.href,
      route: window.location.pathname + window.location.search,
      user_agent: navigator.userAgent || '',
      viewport: { w: window.innerWidth, h: window.innerHeight },
      device_pixel_ratio: window.devicePixelRatio || 1,
      language: navigator.language || 'fr-FR',
      timezone: _safeTimezone(),
      captured_at: new Date().toISOString(),
    },
    // Mode léger (idées/features) : pas de bruit technique console/réseau.
    console_log: light ? [] : consoleRingBuffer.slice(),
    network_failures: light ? [] : networkRingBuffer.slice(),
    app_state: appState,
    screenshot_blob: null,
  };

  if (includeScreenshot) {
    out.screenshot_blob = await _captureScreenshot();
  }
  return out;
}

function _safeTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris'; }
  catch { return 'Europe/Paris'; }
}

async function _captureScreenshot() {
  // Masque visuellement (visibility:hidden) les éléments data-bug-capture-skip
  // avant la capture pour que la page apparaisse "telle qu'elle est vraiment"
  // sans l'overlay du HelpPanel ouvert. visibility:hidden préserve le state
  // React des composants (vs display:none qui démonterait). Au passage, ça
  // double ignoreElements de html2canvas qui ne suffit pas pour les overlays
  // position:fixed (le compositing browser a déjà appliqué l'overlay opaque).
  const hidden = [];
  try {
    const nodes = document.querySelectorAll('[data-bug-capture-skip="true"]');
    nodes.forEach((el) => {
      hidden.push({ el, prev: el.style.visibility });
      el.style.visibility = 'hidden';
    });
  } catch (_) { /* ignore */ }

  try {
    const html2canvasMod = await import(/* webpackChunkName: "html2canvas" */ 'html2canvas');
    const html2canvas = html2canvasMod.default || html2canvasMod;
    const canvas = await html2canvas(document.body, {
      scale: Math.min(window.devicePixelRatio || 1, 2),
      useCORS: true,
      logging: false,
      ignoreElements: (el) => Boolean(el && el.dataset && el.dataset.bugCaptureSkip === 'true'),
    });
    return await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 0.9));
  } catch (err) {
    // Ne pas casser le submit si screenshot échoue (CORS, oklch, etc.).
    // Le bug est signalé sans capture visuelle.
    if (_origConsoleWarn) {
      _origConsoleWarn('[bugReportCapture] screenshot failed:', err && err.message ? err.message : err);
    }
    return null;
  } finally {
    // Restaure la visibility des éléments masqués, quel que soit le résultat.
    hidden.forEach(({ el, prev }) => {
      try { el.style.visibility = prev || ''; } catch (_) { /* ignore */ }
    });
  }
}

// ── Helpers exportés pour tests Jest ──────────────────────────────────────

export function _getConsoleBufferForTests() {
  return consoleRingBuffer.slice();
}

export function _getNetworkBufferForTests() {
  return networkRingBuffer.slice();
}

export function _resetForTests() {
  consoleRingBuffer.length = 0;
  networkRingBuffer.length = 0;
  if (_origConsoleError) console.error = _origConsoleError;
  if (_origConsoleWarn) console.warn = _origConsoleWarn;
  if (_origFetch) window.fetch = _origFetch;
  initialized = false;
  _origConsoleError = null;
  _origConsoleWarn = null;
  _origFetch = null;
}
